/**
 * Postfach-Zuordnung, Zustand A (Phase C.2 WP-D1) — übersetzt die `SignalCode`-Werte und die
 * Vorschlags-`Stufe` aus `apps/api/src/lib/zuordnungs-vorschlag.ts` (WP-B) in Betreibersprache.
 * Eigene, schlanke Kopie des Wertebereichs statt eines Imports quer über den Monorepo-Paketschnitt
 * (`apps/web` importiert nicht aus `apps/api/src` — dieselbe Grenze wie überall sonst im Repo,
 * s. z.B. `ui/format.ts`s eigene `EventStatus`-Kopie statt eines API-seitigen Imports).
 *
 * Reine, DB-freie Übersetzungstabellen — testbar ohne Fixtures (`posteingang-zuordnung.test.tsx`
 * prüft nur, dass die Texte im UI ankommen, nicht diese Tabellen selbst noch einmal separat).
 */
import type { StatusMeta } from "../ui/format";

export type SignalCode =
  | "sender_identity"
  | "single_active"
  | "thread_sent"
  | "subject_number"
  | "date_proximity"
  | "recent_activity";

export type Stufe = "sicher" | "wahrscheinlich";

/** Betreibersprachliche Begründung je Signal — Basis der „Warum?“-Zeile im `vorschlag`-Modus und
 *  der Signal-Chips neben jedem Kandidaten im `auswahl`-Modus. */
export const SIGNAL_TEXT: Record<SignalCode, string> = {
  thread_sent: "Antwort auf eine gesendete Nachricht dieses Vorgangs",
  subject_number: "Vorgangsnummer im Betreff gefunden",
  single_active: "Einziger aktiver Vorgang dieses Absenders",
  date_proximity: "Termin liegt zeitlich nah an dieser Nachricht",
  recent_activity: "Vorgang wurde kürzlich bearbeitet",
  sender_identity: "Absender ist bereits als Kundin/Kunde dieses Vorgangs bekannt",
};

/** Kurzform für die Signal-Chips (Platz ist knapp neben einer Kandidaten-Karte) — dieselbe
 *  Bedeutung wie `SIGNAL_TEXT`, nur kompakter formuliert. */
export const SIGNAL_CHIP_TEXT: Record<SignalCode, string> = {
  thread_sent: "Antwort im Thread",
  subject_number: "Nummer im Betreff",
  single_active: "Einziger aktiver Vorgang",
  date_proximity: "Datum passt",
  recent_activity: "Kürzlich bearbeitet",
  sender_identity: "Bekannter Absender",
};

/** Stufen-Pill (`StatusPill`) im `vorschlag`-Modus — „sicher“/„wahrscheinlich“, nie „unsicher“
 *  (ein unsicherer Vorschlag erreicht diese Anzeige laut `bewerteVorschlag` gar nicht erst). */
export const STUFE_META: Record<Stufe, StatusMeta> = {
  sicher: { label: "Sicher", tone: "success" },
  wahrscheinlich: { label: "Wahrscheinlich", tone: "warn" },
};

/**
 * WP-D2 (Zustand B, „Warum-Panel je Nachricht"): übersetzt EINE Zeile aus
 * `GET /api/messages/:id/zuordnungen` (`message_assignment_log`, Migration 388) in die
 * Chip-/Zeitleisten-Bezeichnung. Werte-Herkunft (Migration 388): `reassign_message`/
 * `assign_message_to_event` schreiben `actor_type='operator'`, `reason_code='manual'`;
 * `assign_unassigned_group_to_event` schreibt `actor_type='operator'`, `reason_code` aus
 * `vorschlag_bestaetigt | manuell_gewaehlt | suche | neuer_vorgang`; `retro_match_unassigned_messages`
 * schreibt `actor_type='regel'`, `reason_code='retro_match'`. `'system'` steht hier zusätzlich für
 * eine künftige WP-G-Automatik (`auto_bind_single_active`) bereit, ohne dass diese Datei dafür
 * geändert werden müsste.
 */
export function zuordnungsArtLabel(eintrag: { actorType: string; reasonCode: string }): string {
  if (eintrag.reasonCode === "vorschlag_bestaetigt") return "Vorschlag bestätigt";
  if (eintrag.actorType === "regel" || eintrag.actorType === "system") return "Automatisch zugeordnet";
  return "Manuell zugeordnet";
}

/** Aktions-Zeile in der Zeitleiste — unterscheidet Erst-/Um-/Ent-Zuordnung anhand der beiden
 *  Event-IDs derselben Audit-Zeile (Migration 388: `from_event_id`/`to_event_id`). */
export function zuordnungsAktionLabel(eintrag: { fromEventId: string | null; toEventId: string | null }): string {
  if (eintrag.toEventId === null) return "Zuordnung aufgehoben";
  if (eintrag.fromEventId === null) return "Erstmalig zugeordnet";
  return "Vorgang gewechselt";
}

/** `message_assignment_log.signals` ist JSONB (`'{}'` bei jeder manuellen Aktion, ein Array
 *  validierter `SignalCode`-Werte bei `assign_unassigned_group_to_event`) — filtert auf bekannte
 *  Codes, damit im Warum-Panel nie eine fremde/zukünftige Zeichenkette ungeprüft landet. */
export function alsSignalCodes(x: unknown): SignalCode[] {
  if (!Array.isArray(x)) return [];
  return x.filter((s): s is SignalCode => typeof s === "string" && s in SIGNAL_CHIP_TEXT);
}
